import React, { FC, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { Loader, PublicError } from '@modernloop/shared/helper-components';
import { logError } from '@modernloop/shared/utils';

import {
  ActivityLogEntityType,
  ActivityType,
  TaskTimelineActivityLogsListQuery_task_Task_activityLogs_ActivityLogsResult_items_ActivityLog as TaskTimelineActivityLogItem,
  TaskTimelineActivityLogsListQuery,
  useActivityLogCreatedSubscription,
  useTaskTimelineActivityLogsListQuery,
  useTaskUpdatedSubscription,
} from 'src/generated/mloop-graphql';

import { ActivityLogLike } from 'src/entities/ActivityLog/types';

import { PAGER_DEFAULT, PAGE_SIZE_100 } from 'src/constants';

import { AGENT_ACTIVITY_LOG_SAFETY_POLL_INTERVAL_MS } from './agentActivityLogSafetyPoll';

import { SCHEDULE_TASK_RELATED_QUERY_NAMES } from 'src/hooks/useRefetchScheduleTaskQuery';

import ActivityLogsList from '.';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActivityLogCreatedSubscriptionGQL = gql`
  subscription ActivityLogCreated($input: ActivityLogSubscriptionInput!) {
    activityLogCreated(input: $input) {
      id
      activityType
      createdAt
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskUpdatedSubscriptionGQL = gql`
  subscription TaskUpdated($input: TaskUpdatedSubscriptionInput!) {
    taskUpdated(input: $input) {
      taskId
      updateType
      delegateStatus
    }
  }
`;

type Props = {
  taskId: string;
  activityTypes?: ActivityType[];
  safetyPollActivityLogs?: boolean;
};

const shouldRefetchScheduleTaskForTaskUpdate = (updateType?: string | null) =>
  updateType != null &&
  [
    'INTERVIEW_PLAN',
    'AVAILABILITY',
    'CANDIDATE_AVAILABILITY',
    'AVAILABILITY_REQUEST',
    'SELF_SCHEDULE_REQUEST',
    'SCHEDULE',
  ].includes(updateType);

/**
 * Fetches task activity logs for the bottom-up timeline surface.
 */
const TaskTimelineActivityLogsList: FC<Props> = ({ taskId, activityTypes, safetyPollActivityLogs = false }) => {
  const [hasLoadedAdditionalActivityLogPages, setHasLoadedAdditionalActivityLogPages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const shouldSafetyPollActivityLogs = safetyPollActivityLogs && !hasLoadedAdditionalActivityLogPages;

  const { data, loading, error, fetchMore } = useTaskTimelineActivityLogsListQuery({
    variables: {
      taskId,
      input: {
        activityTypes,
        pageInput: {
          limit: PAGE_SIZE_100,
        },
      },
    },
    pollInterval: shouldSafetyPollActivityLogs ? AGENT_ACTIVITY_LOG_SAFETY_POLL_INTERVAL_MS : undefined,
  });

  useActivityLogCreatedSubscription({
    variables: { input: { entityType: ActivityLogEntityType.ActivityLogEntityTask, entityId: taskId } },
    onData: ({ data: subData, client }) => {
      const newLog = subData.data?.activityLogCreated;
      if (!newLog) {
        return;
      }

      client.cache.modify({
        id: client.cache.identify({ __typename: 'Task', id: taskId }),
        fields: {
          activityLogs(existing = { items: [], nextCursor: null }) {
            const newRef = client.cache.writeFragment({
              fragment: gql`
                fragment NewTimelineActivityLog on ActivityLog {
                  id
                  activityType
                  createdAt
                }
              `,
              data: newLog,
            });
            return {
              ...existing,
              items: [newRef, ...existing.items],
            };
          },
        },
      });
    },
  });

  useTaskUpdatedSubscription({
    variables: { input: { taskId } },
    onData: ({ data: subData, client }) => {
      const taskUpdated = subData.data?.taskUpdated;
      if (!taskUpdated) {
        return;
      }
      const delegateStatus =
        (taskUpdated as typeof taskUpdated & { delegateStatus?: string | null }).delegateStatus ?? null;

      const cacheId = client.cache.identify({ __typename: 'Task', id: taskId });
      if (cacheId && taskUpdated.updateType === 'DELEGATE_STATUS') {
        client.cache.modify({
          id: cacheId,
          fields: {
            delegateStatus() {
              return delegateStatus;
            },
          },
        });
        return;
      }

      if (taskUpdated.updateType === 'DELEGATE_TYPE') {
        client.refetchQueries({ include: ['ScheduleTask'] });
        return;
      }

      if (shouldRefetchScheduleTaskForTaskUpdate(taskUpdated.updateType)) {
        client.refetchQueries({ include: [...SCHEDULE_TASK_RELATED_QUERY_NAMES] });
      }
    },
  });

  const options = useMemo(
    () => ([...(data?.task?.activityLogs?.items ?? [])] as TaskTimelineActivityLogItem[]).reverse(),
    [data?.task?.activityLogs?.items]
  );

  const loadMoreData = async () => {
    if (!data?.task?.activityLogs?.nextCursor || loading || error || !fetchMore || isLoadingMore) {
      return;
    }

    setHasLoadedAdditionalActivityLogPages(true);
    setIsLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          input: {
            pageInput: {
              limit: PAGER_DEFAULT,
              cursor: data?.task?.activityLogs?.nextCursor,
            },
          },
        },
        updateQuery: (previousQueryResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.task?.id) {
            return previousQueryResult;
          }

          const newOptions = fetchMoreResult.task?.activityLogs.items || [];

          const result = {
            ...previousQueryResult,
            ...fetchMoreResult,
            task: {
              ...previousQueryResult.task,
              ...fetchMoreResult.task,
              id: fetchMoreResult.task.id,
              activityLogs: {
                ...previousQueryResult.task?.activityLogs,
                ...fetchMoreResult.task?.activityLogs,
                items: [...(previousQueryResult.task?.activityLogs.items || []), ...newOptions],
                nextCursor: fetchMoreResult.task?.activityLogs.nextCursor,
              },
            },
          };

          return result as TaskTimelineActivityLogsListQuery;
        },
      });
    } catch (paginationError) {
      logError(paginationError);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (loading && !data?.task?.activityLogs) {
    return <Loader loading />;
  }

  if (error) {
    return <PublicError error={error} />;
  }

  return (
    <ActivityLogsList
      activityLogOrder="OLDEST_FIRST"
      activityLogs={options as unknown as ActivityLogLike[]}
      isLoadingMore={isLoadingMore}
      onViewMoreClick={loadMoreData}
      showViewMoreButton={Boolean(data?.task?.activityLogs?.nextCursor)}
      viewMorePlacement="start"
    />
  );
};

export default TaskTimelineActivityLogsList;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskTimelineActivityLogsListQueryGQL = gql`
  ${ActivityLogsList.fragments.activityLogs}
  fragment TaskTimelineActivityLogsList_activityLog on ActivityLog {
    id
    ...ActivityLogsList_activityLog
  }
  query TaskTimelineActivityLogsList(
    $taskId: uuid!
    $input: ActivityLogsForSourceInput!
    $showTaskName: Boolean! = false
  ) {
    task(id: $taskId) {
      id
      activityLogs(input: $input) {
        items {
          id
          ...TaskTimelineActivityLogsList_activityLog
        }
        nextCursor
      }
    }
  }
`;
